import { create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { CATEGORY, StorageNames } from '@/lib/constants';

export interface T_Coupon {
  name: string;
  id: number | string;
  couponCode: string;
  custom1: string;
  custom2: string;
  custom3: string;
  custom4: string;
  custom5: string;
  description: string;
}

export interface Voucher {
  RequestStatus: string;
  Description: string;
  VoucherStatus: string;
  ProductType: string;
  OfferId: string;
  OfferDescription: string;
  ExpiryDate: string;
  Campaigns: any[];
  ServiceCodes: any[];
  url: string;
}
export interface VoucherDetails {
  voucherCode: string;
  voucher: Voucher;
}

// the store for managing products in the cart
interface CouponStore {
  coupons: T_Coupon[];
  voucherDetails: VoucherDetails | null;
  pendingVoucherTrackCode: string | null;
  pendingVoucherRemoveTrackCode: string | null;
  pendingVoucherRemoveNavigateHome: boolean;
  applyCouponToStore: (coupon: T_Coupon) => Promise<void>;
  applyMultipleCouponsToStore: (coupon: T_Coupon[]) => Promise<void>;
  removeCouponBasedOnId: (coupon: T_Coupon, track?: boolean) => Promise<void>;
  removeCouponBasedOnCouponCode: (couponCode: string, track?: boolean) => void;
  getCouponBasedOnProductId: (custom1: string) => Promise<T_Coupon | undefined>;
  hasSameCouponTypeAndCategory: (coupon: T_Coupon) => boolean;
  removeAllCoupons: () => Promise<void>;
  getCouponFromCode: (couponCode: string) => Promise<T_Coupon | undefined>;
  getCouponList: () => Promise<string[]>;
  removeBroadbandCoupons: () => void;
  updateCouponList: (couponList: T_Coupon[]) => Promise<void>;
  couponBackup: any;
  couponBackUpStore: () => void;
  restoreCouponStore: () => void;
  resetCouponOnly: () => Promise<void>;
  removeNotMatchingCoupons: (couponCode: string) => Promise<void>;
  addVocherDetails: (voucher: VoucherDetails) => void;
  addBackupCouponList: (data: any) => void;
  setPendingVoucherTrackCode: (couponCode: string | null) => void;
  consumePendingVoucherTrackCode: () => string | null;
  setPendingVoucherRemoveTrackCode: (couponCode: string | null) => void;
  consumePendingVoucherRemoveTrackCode: () => string | null;
  setPendingVoucherRemoveNavigateHome: (value: boolean) => void;
  consumePendingVoucherRemoveNavigateHome: () => boolean;
  clearPendingVoucherTrackCode: () => void;
  clearPendingVoucherRemoveTrackCode: () => void;
}

type CouponBackupState = Omit<
  CouponStore,
  | 'couponBackUpStore'
  | 'restoreCouponStore'
  | 'setPendingVoucherTrackCode'
  | 'consumePendingVoucherTrackCode'
  | 'setPendingVoucherRemoveTrackCode'
  | 'consumePendingVoucherRemoveTrackCode'
  | 'setPendingVoucherRemoveNavigateHome'
  | 'consumePendingVoucherRemoveNavigateHome'
  | 'clearPendingVoucherTrackCode'
  | 'clearPendingVoucherRemoveTrackCode'
>;

// Define what part of the state to persist
type PersistCouponState = Pick<CouponStore, 'coupons'>;

const persistOptions: PersistOptions<CouponStore, PersistCouponState> = {
  name: StorageNames.COUPON_STORAGE,
  partialize: (state: CouponStore) => ({
    coupons: state.coupons,
    couponBackup: state.couponBackup,
    voucherDetails: state.voucherDetails,
  }),
};

// Create the Zustand store with persistence
export const useCouponsStore = create<CouponStore>()(
  persist(
    (set, get) => ({
      coupons: [],
      voucherDetails: null,
      pendingVoucherTrackCode: null,
      pendingVoucherRemoveTrackCode: null,
      pendingVoucherRemoveNavigateHome: false,

      couponBackup: null as CouponBackupState | null,

      applyCouponToStore: async (coupon: T_Coupon) => {
        const couponExists = get().coupons.some(
          (c: { couponCode: string }) => c.couponCode === coupon.couponCode
        );
        if (!couponExists) {
          set(state => ({
            coupons: [...state.coupons, coupon],
          }));
        }
      },

      applyMultipleCouponsToStore: async (coupons: T_Coupon[]) => {
        set({ coupons: [...coupons] });
      },

      removeCouponBasedOnId: async (coupon: T_Coupon, track?: boolean) => {
        set(state => ({
          coupons: state.coupons.filter(c => c.id !== coupon.id),
        }));
      },

      removeCouponBasedOnCouponCode: (couponCode: string, track?: boolean) => {
        set(state => ({
          coupons: state.coupons.filter(c => c.couponCode !== couponCode),
        }));
      },

      getCouponBasedOnProductId: async (id: string) => {
        return get().coupons.find((c: { custom1: string }) => c.custom1 === id);
      },

      hasSameCouponTypeAndCategory: (coupon: T_Coupon) => {
        // Sample logic: Replace with real implementation
        return false;
      },

      getCouponFromCode: async (couponCode: string) => {
        return get().coupons.find((c: { couponCode: string }) => c.couponCode === couponCode);
      },

      getCouponList: async () => {
        const couponsList = get().coupons || [];
        return couponsList.map(c => c.couponCode) || [];
      },

      removeBroadbandCoupons: () => {
        const filterCoupons = (coupons: T_Coupon[]) =>
          coupons.filter(p => p.custom5.toLowerCase() !== CATEGORY.BROADBAND.toLowerCase());

        const filteredCoupons = filterCoupons(get().coupons);
        const filteredBackupCoupons = get().couponBackup?.coupons
          ? filterCoupons(get().couponBackup.coupons)
          : [];

        set(state => ({
          coupons: filteredCoupons,
          couponBackup: get().couponBackup
            ? { ...get().couponBackup, coupons: filteredBackupCoupons }
            : null, // If couponBackup is null, leave it as null
        }));
      },

      removeAllCoupons: async () => {
        set({ coupons: [], couponBackup: null, voucherDetails: null });
      },

      resetCouponOnly: async () => {
        set({ coupons: [] });
      },

      updateCouponList: async (couponList: T_Coupon[]) => {
        set({ coupons: [...couponList] });
      },

      removeNotMatchingCoupons: async (couponCode: string) => {
        set(state => ({
          coupons: state.coupons.filter(c => c.couponCode === couponCode),
        }));
      },

      // Backup current state (exclude couponBackup to prevent circular references)
      couponBackUpStore: () => {
        const state = get();
        const {
          couponBackup,
          pendingVoucherTrackCode,
          pendingVoucherRemoveTrackCode,
          pendingVoucherRemoveNavigateHome,
          ...backupState
        } = state; // Exclude transient tracking fields and couponBackup itself
        set({
          couponBackup: { ...backupState },
        });
      },

      // Restore state from backup
      restoreCouponStore: () => {
        const backupState = get().couponBackup;
        if (backupState) {
          set({ ...backupState, couponBackup: null, voucherDetails: null });
        }
      },

      addVocherDetails: (voucher: VoucherDetails) => {
        set({
          voucherDetails: voucher,
        });
      },

      setPendingVoucherTrackCode: (couponCode: string | null) => {
        set({
          pendingVoucherTrackCode: couponCode,
        });
      },

      consumePendingVoucherTrackCode: () => {
        const couponCode = get().pendingVoucherTrackCode;
        set({
          pendingVoucherTrackCode: null,
        });
        return couponCode;
      },

      setPendingVoucherRemoveTrackCode: (couponCode: string | null) => {
        set({
          pendingVoucherRemoveTrackCode: couponCode,
        });
      },

      consumePendingVoucherRemoveTrackCode: () => {
        const couponCode = get().pendingVoucherRemoveTrackCode;
        set({
          pendingVoucherRemoveTrackCode: null,
        });
        return couponCode;
      },

      setPendingVoucherRemoveNavigateHome: (value: boolean) => {
        set({
          pendingVoucherRemoveNavigateHome: value,
        });
      },

      consumePendingVoucherRemoveNavigateHome: () => {
        const shouldNavigateHome = get().pendingVoucherRemoveNavigateHome;
        set({
          pendingVoucherRemoveNavigateHome: false,
        });
        return shouldNavigateHome;
      },

      clearPendingVoucherTrackCode: () => {
        set({
          pendingVoucherTrackCode: null,
        });
      },

      clearPendingVoucherRemoveTrackCode: () => {
        set({
          pendingVoucherRemoveTrackCode: null,
        });
      },

      addBackupCouponList: (data: any) => {
        set({
          couponBackup: { ...data },
        });
      },
    }),
    persistOptions
  )
);
